import { JournalEntryScore } from './model/dto/journal-entry/journal-entry-score';

/**
 * Two deliberate departures from the backend's `SYMPTOM_CATEGORIES`: `bleeding`
 * is added to Body, because it is scored but was never listed there, and
 * `weight_gain` is dropped, because it is listed there but has no score and so
 * could never produce a dot.
 *
 * `symptom.type.ts` and `daily-data-utils.ts` cover the same ground with a
 * legacy vocabulary — `mood`, `energy`, `dreams`, `motivation`. Neither is an
 * authority here.
 */
export type SymptomKey = keyof Omit<
  JournalEntryScore,
  'id' | 'journal_entry' | 'created_at' | 'updated_at'
>;

export type SymptomCategoryKey =
  | 'body'
  | 'diet_and_digestion'
  | 'mental_wellbeing'
  | 'sex'
  | 'sleep'
  | 'skin_and_hair'
  | 'urination';

export type SymptomScaleShape =
  | 'severity'
  | 'severity_extended'
  | 'directional'
  | 'non_monotonic'
  | 'short';

export type SymptomIntensityLogic = 'severity' | 'directional';

export type IntensityLogicFor<S extends SymptomScaleShape> =
  S extends 'directional' ? 'directional' : 'severity';

export interface SymptomValue {
  label: string;
  score: number;
}

export interface SymptomDefinition<
  S extends SymptomScaleShape = SymptomScaleShape
> {
  key: SymptomKey;
  category: SymptomCategoryKey;
  labelKey: string;
  scaleShape: S;
  intensityLogic: IntensityLogicFor<S>;
  minScore: number;
  maxScore: number;
  isMultiSelect: boolean;
  values: SymptomValue[];
}

export const SYMPTOM_CATEGORY_ORDER: SymptomCategoryKey[] = [
  'body',
  'diet_and_digestion',
  'mental_wellbeing',
  'sex',
  'sleep',
  'skin_and_hair',
  'urination',
];

export const SYMPTOM_CATEGORY_LABEL_KEYS: Record<SymptomCategoryKey, string> = {
  body: 'hormoneTrends.symptomCategories.body',
  diet_and_digestion: 'hormoneTrends.symptomCategories.dietAndDigestion',
  mental_wellbeing: 'hormoneTrends.symptomCategories.mentalWellbeing',
  sex: 'hormoneTrends.symptomCategories.sex',
  sleep: 'hormoneTrends.symptomCategories.sleep',
  skin_and_hair: 'hormoneTrends.symptomCategories.skinAndHair',
  urination: 'hormoneTrends.symptomCategories.urination',
};

const severityValues: SymptomValue[] = [
  { label: 'None', score: 0 },
  { label: 'Mild', score: 1 },
  { label: 'Moderate', score: 2 },
  { label: 'Severe', score: 3 },
];

const directionalValues: SymptomValue[] = [
  { label: 'Very Positive', score: 0 },
  { label: 'Positive', score: 1 },
  { label: 'Normal', score: 2 },
  { label: 'Negative', score: 3 },
  { label: 'Very negative', score: 4 },
];

interface SymptomDefinitionInput<S extends SymptomScaleShape> {
  category: SymptomCategoryKey;
  labelKey: string;
  scaleShape: S;
  values: SymptomValue[];
  isMultiSelect?: boolean;
}

const define = <S extends SymptomScaleShape>(
  key: SymptomKey,
  input: SymptomDefinitionInput<S>
): SymptomDefinition<S> => {
  const scores = input.values.map((value) => value.score);

  return {
    key,
    category: input.category,
    labelKey: input.labelKey,
    scaleShape: input.scaleShape,
    intensityLogic: (input.scaleShape === 'directional'
      ? 'directional'
      : 'severity') as IntensityLogicFor<S>,
    minScore: Math.min(...scores),
    maxScore: Math.max(...scores),
    isMultiSelect: input.isMultiSelect ?? false,
    values: [...input.values].sort((a, b) => a.score - b.score),
  };
};

/**
 * Declared with `satisfies` rather than an annotation so each entry keeps its
 * literal scale shape. That is what lets `DirectionalSymptomKey` below be read
 * off the data instead of being retyped by hand.
 */
export const SYMPTOM_CATALOGUE = {
  bleeding: define('bleeding', {
    category: 'body',
    labelKey: 'hormoneTrends.symptoms.bleeding',
    scaleShape: 'severity_extended',
    values: [
      { label: 'None', score: 0 },
      { label: 'Spotting', score: 1 },
      { label: 'Light', score: 2 },
      { label: 'Medium', score: 3 },
      { label: 'Heavy', score: 4 },
    ],
  }),
  hot_flashes: define('hot_flashes', {
    category: 'body',
    labelKey: 'hormoneTrends.symptoms.hotFlashes',
    scaleShape: 'severity',
    values: severityValues,
  }),
  breast_tenderness: define('breast_tenderness', {
    category: 'body',
    labelKey: 'hormoneTrends.symptoms.breastTenderness',
    scaleShape: 'severity',
    values: severityValues,
  }),
  nausea: define('nausea', {
    category: 'body',
    labelKey: 'hormoneTrends.symptoms.nausea',
    scaleShape: 'severity',
    values: severityValues,
  }),
  cramps: define('cramps', {
    category: 'body',
    labelKey: 'hormoneTrends.symptoms.cramps',
    scaleShape: 'severity',
    values: severityValues,
  }),
  joint_pain: define('joint_pain', {
    category: 'body',
    labelKey: 'hormoneTrends.symptoms.jointPain',
    scaleShape: 'severity',
    values: severityValues,
  }),
  back_pain: define('back_pain', {
    category: 'body',
    labelKey: 'hormoneTrends.symptoms.backPain',
    scaleShape: 'severity',
    values: severityValues,
  }),
  headache: define('headache', {
    category: 'body',
    labelKey: 'hormoneTrends.symptoms.headache',
    scaleShape: 'severity',
    values: severityValues,
  }),
  migraine: define('migraine', {
    category: 'body',
    labelKey: 'hormoneTrends.symptoms.migraine',
    scaleShape: 'severity',
    values: severityValues,
  }),
  heart_palpitations_or_racing_heart: define(
    'heart_palpitations_or_racing_heart',
    {
      category: 'body',
      labelKey: 'hormoneTrends.symptoms.heartPalpitationsOrRacingHeart',
      scaleShape: 'severity',
      values: severityValues,
    }
  ),
  dizziness: define('dizziness', {
    category: 'body',
    labelKey: 'hormoneTrends.symptoms.dizziness',
    scaleShape: 'severity',
    values: severityValues,
  }),

  stool: define('stool', {
    category: 'diet_and_digestion',
    labelKey: 'hormoneTrends.symptoms.stool',
    scaleShape: 'short',
    values: [
      { label: 'Normal', score: 0 },
      { label: 'Soft', score: 1 },
      { label: 'Firm', score: 1 },
      { label: 'Watery', score: 2 },
      { label: 'Rock hard', score: 2 },
    ],
  }),
  digestion: define('digestion', {
    category: 'diet_and_digestion',
    labelKey: 'hormoneTrends.symptoms.digestion',
    scaleShape: 'severity',
    values: [
      { label: 'Normal', score: 0 },
      { label: 'Bloating/Gas', score: 1 },
      { label: 'Heartburn', score: 1 },
      { label: 'Nausea', score: 2 },
      { label: 'Vomiting', score: 3 },
    ],
  }),
  bloating: define('bloating', {
    category: 'diet_and_digestion',
    labelKey: 'hormoneTrends.symptoms.bloating',
    scaleShape: 'severity',
    values: severityValues,
  }),

  overall_mood: define('overall_mood', {
    category: 'mental_wellbeing',
    labelKey: 'hormoneTrends.symptoms.overallMood',
    scaleShape: 'directional',
    isMultiSelect: true,
    values: [
      { label: 'Very positive', score: 0 },
      { label: 'Positive', score: 1 },
      { label: 'Neutral', score: 2 },
      { label: 'Negative', score: 3 },
      { label: 'Very negative', score: 4 },
    ],
  }),
  anxiety: define('anxiety', {
    category: 'mental_wellbeing',
    labelKey: 'hormoneTrends.symptoms.anxiety',
    scaleShape: 'severity',
    values: severityValues,
  }),
  motivation_level: define('motivation_level', {
    category: 'mental_wellbeing',
    labelKey: 'hormoneTrends.symptoms.motivationLevel',
    scaleShape: 'directional',
    values: directionalValues,
  }),
  productivity_level: define('productivity_level', {
    category: 'mental_wellbeing',
    labelKey: 'hormoneTrends.symptoms.productivityLevel',
    scaleShape: 'directional',
    values: directionalValues,
  }),
  focus: define('focus', {
    category: 'mental_wellbeing',
    labelKey: 'hormoneTrends.symptoms.focus',
    scaleShape: 'directional',
    values: directionalValues,
  }),
  brain_fog: define('brain_fog', {
    category: 'mental_wellbeing',
    labelKey: 'hormoneTrends.symptoms.brainFog',
    scaleShape: 'severity',
    values: severityValues,
  }),
  memory_problems: define('memory_problems', {
    category: 'mental_wellbeing',
    labelKey: 'hormoneTrends.symptoms.memoryProblems',
    scaleShape: 'severity',
    values: severityValues,
  }),
  energy_level: define('energy_level', {
    category: 'mental_wellbeing',
    labelKey: 'hormoneTrends.symptoms.energyLevel',
    scaleShape: 'directional',
    values: [
      { label: 'Very High', score: 0 },
      { label: 'High', score: 1 },
      { label: 'Normal', score: 2 },
      { label: 'Low', score: 3 },
      { label: 'Exhausted', score: 4 },
    ],
  }),
  emotional_stability: define('emotional_stability', {
    category: 'mental_wellbeing',
    labelKey: 'hormoneTrends.symptoms.emotionalStability',
    scaleShape: 'directional',
    values: directionalValues,
  }),
  stress: define('stress', {
    category: 'mental_wellbeing',
    labelKey: 'hormoneTrends.symptoms.stress',
    scaleShape: 'severity',
    values: severityValues,
  }),

  painful_sex: define('painful_sex', {
    category: 'sex',
    labelKey: 'hormoneTrends.symptoms.painfulSex',
    scaleShape: 'severity',
    values: severityValues,
  }),
  sex_drive: define('sex_drive', {
    category: 'sex',
    labelKey: 'hormoneTrends.symptoms.sexDrive',
    scaleShape: 'directional',
    values: directionalValues,
  }),

  hours_of_sleep: define('hours_of_sleep', {
    category: 'sleep',
    labelKey: 'hormoneTrends.symptoms.hoursOfSleep',
    scaleShape: 'non_monotonic',
    values: [
      { label: '6-9 hrs', score: 0 },
      { label: '3-6 hrs', score: 1 },
      { label: '9+ hrs', score: 2 },
      { label: '0-3 hrs', score: 3 },
    ],
  }),
  sleep_quality: define('sleep_quality', {
    category: 'sleep',
    labelKey: 'hormoneTrends.symptoms.sleepQuality',
    scaleShape: 'short',
    values: [
      { label: 'Good quality', score: 0 },
      { label: 'Restless', score: 1 },
      { label: 'Trouble falling asleep', score: 2 },
      { label: 'Trouble staying asleep', score: 2 },
    ],
  }),
  dream_type: define('dream_type', {
    category: 'sleep',
    labelKey: 'hormoneTrends.symptoms.dreamType',
    scaleShape: 'severity',
    isMultiSelect: true,
    values: [
      { label: 'Soothing', score: 0 },
      { label: 'Happy', score: 0 },
      { label: 'Vivid', score: 1 },
      { label: 'Dark', score: 2 },
      { label: 'Stressful', score: 2 },
      { label: 'Disturbing', score: 3 },
    ],
  }),
  night_sweats: define('night_sweats', {
    category: 'sleep',
    labelKey: 'hormoneTrends.symptoms.nightSweats',
    scaleShape: 'severity',
    values: severityValues,
  }),

  hair_loss_or_thinning: define('hair_loss_or_thinning', {
    category: 'skin_and_hair',
    labelKey: 'hormoneTrends.symptoms.hairLossOrThinning',
    scaleShape: 'severity',
    values: [
      { label: 'No change', score: 0 },
      { label: 'Mild', score: 1 },
      { label: 'Moderate', score: 2 },
      { label: 'Severe', score: 3 },
    ],
  }),
  body_hair_growth: define('body_hair_growth', {
    category: 'skin_and_hair',
    labelKey: 'hormoneTrends.symptoms.bodyHairGrowth',
    scaleShape: 'short',
    isMultiSelect: true,
    values: [
      { label: 'No change', score: 0 },
      { label: 'Decrease', score: 1 },
      { label: 'Mild increase, fine hair', score: 1 },
      { label: 'Noticeable increase, coarse, darker hair', score: 2 },
    ],
  }),
  acne: define('acne', {
    category: 'skin_and_hair',
    labelKey: 'hormoneTrends.symptoms.acne',
    scaleShape: 'severity_extended',
    values: [
      { label: 'None', score: 0 },
      { label: 'Mild', score: 1 },
      { label: 'Moderate', score: 2 },
      { label: 'Severe', score: 3 },
      { label: 'Cystic', score: 4 },
    ],
  }),
  skin_dryness_and_itching: define('skin_dryness_and_itching', {
    category: 'skin_and_hair',
    labelKey: 'hormoneTrends.symptoms.skinDrynessAndItching',
    scaleShape: 'severity',
    values: severityValues,
  }),
  face_sagging_and_wrinkles: define('face_sagging_and_wrinkles', {
    category: 'skin_and_hair',
    labelKey: 'hormoneTrends.symptoms.faceSaggingAndWrinkles',
    scaleShape: 'severity',
    values: severityValues,
  }),

  painful_urination: define('painful_urination', {
    category: 'urination',
    labelKey: 'hormoneTrends.symptoms.painfulUrination',
    scaleShape: 'severity',
    values: severityValues,
  }),
  urination_frequency: define('urination_frequency', {
    category: 'urination',
    labelKey: 'hormoneTrends.symptoms.urinationFrequency',
    scaleShape: 'directional',
    values: directionalValues,
  }),
  incontinence: define('incontinence', {
    category: 'urination',
    labelKey: 'hormoneTrends.symptoms.incontinence',
    scaleShape: 'severity',
    values: severityValues,
  }),
  vaginal_dryness: define('vaginal_dryness', {
    category: 'urination',
    labelKey: 'hormoneTrends.symptoms.vaginalDryness',
    scaleShape: 'severity',
    values: severityValues,
  }),
  uti_symptoms: define('uti_symptoms', {
    category: 'urination',
    labelKey: 'hormoneTrends.symptoms.utiSymptoms',
    scaleShape: 'severity',
    values: severityValues,
  }),
} satisfies Record<SymptomKey, SymptomDefinition>;

export type DirectionalSymptomKey = {
  [K in SymptomKey]: (typeof SYMPTOM_CATALOGUE)[K]['intensityLogic'] extends 'directional'
    ? K
    : never;
}[SymptomKey];

export type SeveritySymptomKey = Exclude<SymptomKey, DirectionalSymptomKey>;

export const isDirectionalSymptom = (
  symptom: SymptomKey
): symptom is DirectionalSymptomKey =>
  SYMPTOM_CATALOGUE[symptom].intensityLogic === 'directional';

export const SYMPTOM_ORDER: SymptomKey[] = (
  Object.keys(SYMPTOM_CATALOGUE) as SymptomKey[]
).sort(
  (a, b) =>
    SYMPTOM_CATEGORY_ORDER.indexOf(SYMPTOM_CATALOGUE[a].category) -
    SYMPTOM_CATEGORY_ORDER.indexOf(SYMPTOM_CATALOGUE[b].category)
);

export const getSymptomDefinition = (key: SymptomKey): SymptomDefinition =>
  SYMPTOM_CATALOGUE[key];

export const getSymptomsInCategory = (
  category: SymptomCategoryKey
): SymptomDefinition[] =>
  SYMPTOM_ORDER.map(getSymptomDefinition).filter(
    (definition) => definition.category === category
  );

export const getSymptomValueLabels = (
  key: SymptomKey,
  score: number
): string[] =>
  SYMPTOM_CATALOGUE[key].values
    .filter((value) => value.score === score)
    .map((value) => value.label);
